import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useStyles, useDeleteStyle, useReorderStyles, type Style } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Pencil, Trash2, ImageIcon, GripVertical, Save, X, ArrowUpDown } from 'lucide-react'
import { toast } from 'sonner'

interface SortableRowProps {
  style: Style
  isReorderMode: boolean
  onNavigate: (id: string) => void
  onDelete: (id: string, name: string) => void
}

function SortableRow({ style, isReorderMode, onNavigate, onDelete }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: style.id, disabled: !isReorderMode })

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f9fafb' : undefined,
  }

  return (
    <tr
      ref={setNodeRef}
      style={rowStyle}
      className={`hover:bg-gray-50 ${isReorderMode ? '' : 'cursor-pointer'}`}
      onClick={() => !isReorderMode && onNavigate(style.id)}
    >
      <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
        {isReorderMode ? (
          <button
            {...attributes}
            {...listeners}
            className="p-1 rounded hover:bg-gray-100 cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </button>
        ) : (
          <div className="p-1">
            <GripVertical className="w-4 h-4 text-slate-200" />
          </div>
        )}
      </td>
      <td className="px-6 py-4">
        {style.thumbnailUrl ? (
          <img
            src={`${style.thumbnailUrl}?v=${new Date(style.updatedAt).getTime()}`}
            alt={style.name}
            className="w-10 h-10 object-cover rounded border"
          />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-gray-400" />
          </div>
        )}
      </td>
      <td className="px-6 py-4 font-mono text-sm">{style.id}</td>
      <td className="px-6 py-4">
        <div className="font-medium text-gray-900">{style.name}</div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-700 line-clamp-2">
        {style.description}
      </td>
      <td className="px-6 py-4 text-sm">{style.listOrder}</td>
      <td className="px-6 py-4">
        <Badge variant={style.enabled ? 'success' : 'secondary'}>
          {style.enabled ? '활성' : '비활성'}
        </Badge>
      </td>
      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        {!isReorderMode && (
          <div className="flex items-center justify-end gap-2">
            <Link to={`/admin/styles/${style.id}/edit`}>
              <Button variant="ghost" size="sm">
                <Pencil className="w-4 h-4" />
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(style.id, style.name)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </td>
    </tr>
  )
}

export function StyleList() {
  const navigate = useNavigate()
  const { data: styles, isLoading, error } = useStyles()
  const deleteMutation = useDeleteStyle()
  const reorderMutation = useReorderStyles()

  const [isReorderMode, setIsReorderMode] = useState(false)
  const [localStyles, setLocalStyles] = useState<Style[] | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const displayStyles = localStyles ?? styles

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`"${name}" 스타일을 삭제하시겠습니까?`)) return

    try {
      await deleteMutation.mutateAsync(id)
      toast.success('스타일이 삭제되었습니다')
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '삭제 중 오류가 발생했습니다')
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !displayStyles) return

    const oldIndex = displayStyles.findIndex((s) => s.id === active.id)
    const newIndex = displayStyles.findIndex((s) => s.id === over.id)

    const newOrder = arrayMove(displayStyles, oldIndex, newIndex)
    setLocalStyles(newOrder)
  }

  const handleStartReorder = () => {
    setLocalStyles(styles ?? null)
    setIsReorderMode(true)
  }

  const handleCancelReorder = () => {
    setLocalStyles(null)
    setIsReorderMode(false)
  }

  const handleSaveReorder = async () => {
    if (!localStyles) return

    try {
      await reorderMutation.mutateAsync(localStyles.map((s) => s.id))
      toast.success('순서가 저장되었습니다')
      setLocalStyles(null)
      setIsReorderMode(false)
    } catch (error: unknown) {
      toast.error(`순서 저장 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }

  if (isLoading) return <div className="text-center py-12">로딩 중...</div>
  if (error) return <div className="text-center py-12 text-red-500">에러: {error.message}</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">스타일</h1>
          <p className="text-caption mt-2 leading-relaxed">이미지 스타일 프리셋 관리</p>
        </div>
        <div className="flex items-center gap-2">
          {isReorderMode ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancelReorder}
                disabled={reorderMutation.isPending}
              >
                <X className="w-4 h-4 mr-2" />
                취소
              </Button>
              <Button
                onClick={handleSaveReorder}
                disabled={reorderMutation.isPending}
              >
                <Save className="w-4 h-4 mr-2" />
                {reorderMutation.isPending ? '저장 중...' : '저장'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleStartReorder}>
                <ArrowUpDown className="w-4 h-4 mr-2" />
                순서 변경
              </Button>
              <Link to="/admin/styles/new">
                <Button>+ 새 스타일</Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {isReorderMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
          드래그하여 순서를 변경한 후 저장 버튼을 눌러주세요.
        </div>
      )}

      <div className="bg-white rounded-lg border min-w-[800px]">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">
                  {/* Drag handle column */}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  썸네일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  이름
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  설명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  순서
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  상태
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  액션
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {displayStyles && (
                <SortableContext
                  items={displayStyles.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {displayStyles.map((style) => (
                    <SortableRow
                      key={style.id}
                      style={style}
                      isReorderMode={isReorderMode}
                      onNavigate={(id) => navigate(`/admin/styles/${id}/view`)}
                      onDelete={handleDelete}
                    />
                  ))}
                </SortableContext>
              )}
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  )
}
